import qs from "qs";
import Vue from "vue";
import Router from "vue-router";
import * as Routes from "../constants/routes";
import layouts from "../layout";
import store from "../store/index";
import AuthUtils from "./../utils/auth";
import routes from "./routes";

Vue.use(Router);

const toastBus = new Vue();

// Make it available globally
window.toastBus = toastBus;

const router = new Router({
    mode: "history",
    routes,
    parseQuery(query) {
        return qs.parse(query);
    },
    stringifyQuery(query) {
        let result = qs.stringify(query);
        return result ? "?" + result : "";
    }
});

// Get the util instance after router is created
const { getRoutesUtil } = require("./../utils/routes.default");
const routes_util = getRoutesUtil(router);

const l = {
    contentOnly() {
        store.commit("setLayout", layouts.contentOnly);
    },
    navLeft() {
        store.commit("setLayout", layouts.navLeft);
    },
    navRight() {
        store.commit("setLayout", layouts.navRight);
    },
    navTop() {
        store.commit("setLayout", layouts.navTop);
    },
    navBottom() {
        store.commit("setLayout", layouts.navBottom);
    },
    set(layout) {
        store.commit("setLayout", layout);
    }
};

//insert here login logic
const auth = {
    loggedIn() {
        return !!store.state.user;
    },
    logout() {
        store.commit("setLogout");
    }
};

/* A router guard to check authentication and permissions */
router.beforeEach((to, from, next) => {
    try {
        // FIRST: Special cases - always let these routes pass through
        // Special handling for error/access denied routes to break recursion
        if (to.name === Routes.ACCESS_DENIED) {
            return next();
        }

        // Set default variables
        let auth_required = false;
        let guest = false;
        let requiredRoles = null;

        // Extract meta data from matched routes
        for (const route of to.matched) {
            if (route.meta) {
                if (route.meta.auth) auth_required = true;
                if (route.meta.guest) guest = true;

                // Check for role restrictions
                if (route.meta && route.meta.roles) {
                    requiredRoles = route.meta.roles;
                }
            }
        }

        // Early layout setting (set layout before any navigation decisions)
        if (to && to.meta && to.meta.layout) {
            l.set(to.meta.layout);
        }

        // Special case: always allow access to error pages without further checks
        if (to.name === Routes.ACCESS_DENIED || to.name === Routes.ERROR_PAGE) {
            return next();
        }

        // SECOND: Handle redirect query parameter
        // If we have a redirect parameter and we're logged in, try to honor it
        // But only if we're not already processing an access control rule
        if (to.query.redirect && auth.loggedIn() && !from.query.redirect) {
            const redirectPath = to.query.redirect;
            // Remove the redirect parameter to prevent loops
            const redirectTo = { path: redirectPath, query: { ...to.query } };
            delete redirectTo.query.redirect;

            // Redirect only if the target is different than where we came from
            if (redirectPath !== from.fullPath) {
                return next(redirectTo);
            }
        }

        // THIRD: Check if route is accessible
        if (!routes_util.isRouteAccessible(to.name)) {
            if (auth.loggedIn()) {
                return next(
                    store.state.current_community
                        ? { name: Routes.HOME }
                        : { name: Routes.COMMUNITIES }
                );
            } else {
                return next({ name: Routes.LOGIN });
            }
        }

        // Handle authentication requirements
        if (auth_required) {
            // User is logged in
            if (auth.loggedIn()) {
                // Check role-based permissions
                if (requiredRoles && !AuthUtils.hasRole(requiredRoles)) {
                    // Use Vue nextTick instead of setTimeout
                    Vue.nextTick(() => {
                        toastBus.$emit("show-toast", {
                            title: "Access Denied",
                            message:
                                "You don't have permission to view this page",
                            type: "warning"
                        });
                    });

                    // Redirect to access denied page
                    return next({ name: Routes.ACCESS_DENIED });
                    //return next({ name: Routes.DASHBOARD });
                }

                // Handle policy agreement
                if (store.state.require_agree_policy) {
                    window.Bus.$emit("openPolicyDialog");
                }

                // Handle home route special case - but prevent loops
                if (to.name === Routes.HOME && !to.query.redirect) {
                    const targetName = store.state.current_community
                        ? Routes.DASHBOARD
                        : Routes.ORDER_HUB;

                    // Only redirect if we're not coming from the target route
                    if (from.name !== targetName) {
                        return next({ name: targetName });
                    }
                }

                // Continue normal navigation
                return next();
            }
            // User is not logged in but route requires auth
            else {
                return next(
                    to.name === Routes.LOGIN
                        ? undefined
                        : {
                              name: Routes.LOGIN,
                              query: { redirect: to.fullPath }
                          }
                );
            }
        }
        // Guest routes
        else {
            // Redirect logged-in users away from guest routes - but prevent loops
            if (
                (guest && auth.loggedIn()) ||
                (auth.loggedIn() && to.name === Routes.LOGIN)
            ) {
                // Only redirect if we're not coming from HOME
                if (from.name !== Routes.HOME) {
                    return next({ name: Routes.HOME });
                }
            }

            // Allow navigation
            return next();
        }
    } catch (error) {
        console.error("Router navigation error:", error);
        return next(false); // Cancel navigation on error
    }
});

// Add route prefetching
router.beforeResolve((to, from, next) => {
    // Components that have a static prefetch method can prefetch data
    const matched = router.getMatchedComponents(to);

    Promise.all(
        matched.map((component) => {
            if (component.prefetch) {
                return component.prefetch(store, to.params);
            }
            return Promise.resolve();
        })
    )
        .then(() => {
            next();
        })
        .catch(next);
});

// Also fix the afterEach handler
router.afterEach(() => {
    // Use nextTick instead of setTimeout to be more Vue-friendly
    Vue.nextTick(() => {
        store.commit("setSplashScreen", false);
    });
});

export default router;
